// client/src/lib/push.js
import { apiFetch, refreshSessionUser } from './api';

// ──────────────────────────────────────────────────────────────
// VAPID key configuration
// ──────────────────────────────────────────────────────────────
//
// 1) Preferred: REACT_APP_VAPID_PUBLIC (Amplify env var)
// 2) Fallback: hardcoded public key (public key is safe to expose)
//

const ENV_VAPID_PUBLIC =
  (typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_VAPID_PUBLIC) ||
  '';

const HARDCODED_VAPID_PUBLIC =
  'BHV2Q7rP9c4bUBtIzOmYUnLfQJ8ilM3dtbv7IjfHc1PbVy6mTWpSpRk7hb16Z7eNODI18f4Hs5nf5Zlx0vh7hSM';

const EFFECTIVE_VAPID_PUBLIC = ENV_VAPID_PUBLIC || HARDCODED_VAPID_PUBLIC || '';

// Prefer reading the public VAPID key from the server (keeps client/server in sync).
// Falls back to env/hardcoded value if the endpoint is unavailable.
let CACHED_SERVER_VAPID_PUBLIC = null;
async function fetchVapidPublicKey() {
  if (CACHED_SERVER_VAPID_PUBLIC) return CACHED_SERVER_VAPID_PUBLIC;
  try {
    const res = await apiFetch('/api/push/vapid-public-key', { credentials: 'include' });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data && typeof data.publicKey === 'string' && data.publicKey.trim()) {
      CACHED_SERVER_VAPID_PUBLIC = data.publicKey.trim();
      return CACHED_SERVER_VAPID_PUBLIC;
    }
  } catch {
    // ignore and fall back
  }
  return '';
}

// ──────────────────────────────────────────────────────────────
// Capability checks
// ──────────────────────────────────────────────────────────────
export function isPushSupported() {
  if (typeof window === 'undefined') return false;

  const hasSW = 'serviceWorker' in navigator;
  const hasPush = 'PushManager' in window;
  const hasNotif = 'Notification' in window;

  const hostname = window.location.hostname;
  const protocol = window.location.protocol;

  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  const isSecure = protocol === 'https:' || isLocalhost;

  return hasSW && hasPush && hasNotif && isSecure;
}

// iOS helpers (for “install to home screen” requirement)
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

// ──────────────────────────────────────────────────────────────
// Utilities
// ──────────────────────────────────────────────────────────────
function urlBase64ToUint8Array(base64) {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function getVapidPublicKeyFallback() {
  return EFFECTIVE_VAPID_PUBLIC;
}

function normalizePublicUrl(raw) {
  const v = (raw || '').trim();
  if (v === '/' || v === './') return '';
  return v.replace(/\/$/, '');
}

function wait(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function waitForVisible() {
  if (document.visibilityState === 'visible') return Promise.resolve();
  return new Promise((resolve) => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', onVis);
        resolve();
      }
    };
    document.addEventListener('visibilitychange', onVis);
  });
}

function waitForFocus() {
  try {
    if (typeof document.hasFocus === 'function' && document.hasFocus()) return Promise.resolve();
  } catch {
    // ignore
  }
  return new Promise((resolve) => {
    const onFocus = () => {
      window.removeEventListener('focus', onFocus);
      resolve();
    };
    window.addEventListener('focus', onFocus, { once: true });
  });
}

function waitForPrerenderingDone() {
  if (!('prerendering' in document)) return Promise.resolve();
  if (!document.prerendering) return Promise.resolve();
  return new Promise((resolve) => {
    const onChange = () => {
      if (!document.prerendering) resolve();
    };
    document.addEventListener('prerenderingchange', onChange, { once: true });
  });
}

function waitForLoad() {
  if (document.readyState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true });
  });
}

async function waitForSwActive(reg, timeoutMs = 5000) {
  try {
    if (reg?.active) return true;

    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      if (reg?.active) return true;
      await wait(100);
    }
  } catch {
    // ignore
  }
  return !!reg?.active;
}

// ──────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────

/**
 * Ensure we have a registered Service Worker and a stored push subscription
 * for the currently logged-in user. Call this AFTER login.
 *
 * Returns: { ok: boolean, reason?: string, status?: number, errorName?: string, errorMessage?: string }
 * reason:
 *  - unsupported
 *  - ios-install-required
 *  - denied
 *  - no-key
 *  - sw-missing
 *  - sw-html
 *  - server
 *  - exception
 */
export async function registerPush() {
  try {
    if (!isPushSupported()) {
      return { ok: false, reason: 'unsupported' };
    }

    // iOS Safari must be installed to Home Screen for Web Push
    if (isIOS() && !isStandalone()) {
      return { ok: false, reason: 'ios-install-required' };
    }

    // Avoid InvalidStateError on Chrome (prerender/hidden/BFCache edge cases)
    await waitForPrerenderingDone();
    await waitForVisible();
    await waitForFocus();
    await waitForLoad();

    // Give the browser a tick after a user gesture / focus change before SW APIs
    await wait(50);

    // Register service worker from the correct base path (supports Amplify subpaths / PUBLIC_URL)
    const base = normalizePublicUrl(process.env.PUBLIC_URL);
    const swUrl = `${base}/sw.js`;
    const scope = base ? `${base}/` : '/';

    // Clean up stale registrations from older scopes (helps after redeploys / path changes)
    // If SW APIs throw InvalidStateError, skip cleanup and continue — registration may still work.
    try {
      const expectedScopeAbs = new URL(scope, window.location.origin).href;
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        regs
          .filter((r) => r && r.scope && r.scope !== expectedScopeAbs)
          .map((r) => r.unregister().catch(() => {}))
      );
    } catch (e) {
      const name = e?.name || '';
      const msg = String(e?.message || '');
      if (!(name === 'InvalidStateError' || /invalid state/i.test(msg))) {
        // keep non-InvalidState errors visible in console for debugging
        console.warn('[push] stale SW cleanup skipped:', e);
      }
    }

    // Preflight: if /sw.js is being rewritten to index.html, registration will fail.
    // Detect that early and return a clearer reason for the UI.
    try {
      const resp = await fetch(swUrl, { cache: 'no-store' });
      if (!resp.ok) {
        console.warn('[push] sw fetch failed', resp.status, swUrl);
        return { ok: false, reason: 'sw-missing' };
      }
      const head = (await resp.text()).slice(0, 200).toLowerCase();
      if (head.includes('<!doctype') || head.includes('<html')) {
        console.warn('[push] sw looks like HTML (likely SPA rewrite). Check Amplify rewrites for', swUrl);
        return { ok: false, reason: 'sw-html' };
      }
    } catch {
      // ignore preflight failures; registration may still succeed
    }

    let reg = null;
    try {
      reg =
        (await navigator.serviceWorker.getRegistration(scope)) ||
        (await navigator.serviceWorker.register(swUrl, { scope }));
    } catch (err) {
      // Retries for InvalidStateError (Chrome can still throw briefly after focus/visibility changes)
      const name = err?.name || '';
      const msg = String(err?.message || '');
      const isInvalidState = name === 'InvalidStateError' || /invalid state/i.test(msg);
      if (!isInvalidState) throw err;

      // retry #1
      await wait(350);
      await waitForPrerenderingDone();
      await waitForVisible();
      await waitForFocus();
      await waitForLoad();
      await wait(50);
      try {
        reg =
          (await navigator.serviceWorker.getRegistration(scope)) ||
          (await navigator.serviceWorker.register(swUrl, { scope }));
      } catch (err2) {
        const name2 = err2?.name || '';
        const msg2 = String(err2?.message || '');
        const isInvalidState2 = name2 === 'InvalidStateError' || /invalid state/i.test(msg2);
        if (!isInvalidState2) throw err2;

        // retry #2 (final)
        await wait(650);
        await waitForPrerenderingDone();
        await waitForVisible();
        await waitForFocus();
        await waitForLoad();
        await wait(100);
        reg =
          (await navigator.serviceWorker.getRegistration(scope)) ||
          (await navigator.serviceWorker.register(swUrl, { scope }));
      }
    }

    // Ensure the SW is active before subscribing (reduces flakiness on some devices)
    await waitForSwActive(reg, 5000);

    // If already subscribed, just send it to the server again to keep it in sync
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      const persistedExisting = await persistSubscription(existing);
      return persistedExisting.ok ? { ok: true } : persistedExisting;
    }

    // Ask for permission if needed
    let perm = Notification.permission;
    if (perm === 'default') {
      perm = await Notification.requestPermission();
    }
    if (perm !== 'granted') {
      return { ok: false, reason: 'denied' };
    }

    let vapid = await fetchVapidPublicKey();
    if (!vapid) vapid = getVapidPublicKeyFallback();
    if (!vapid) {
      console.warn('[push] No VAPID public key configured');
      return { ok: false, reason: 'no-key' };
    }

    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });

    const persisted = await persistSubscription(sub);
    return persisted.ok ? { ok: true } : persisted;
  } catch (err) {
    console.warn('[push] register error', err);
    return {
      ok: false,
      reason: 'exception',
      errorName: err?.name || 'Error',
      errorMessage: String(err?.message || err || ''),
    };
  }
}

/**
 * Remove the current SW subscription and tell the server to forget it.
 * Safe to call even if no subscription exists.
 *
 * Options:
 *  - unregisterServiceWorker: also unregister SW registrations (strong reset)
 */
export async function unregisterPush(options = {}) {
  const { unregisterServiceWorker = false } = options || {};
  if (!('serviceWorker' in navigator)) return { ok: true };

  try {
    const regs = await navigator.serviceWorker.getRegistrations?.();
    const allRegs = Array.isArray(regs) ? regs : [];

    // Prefer current registration if any
    const reg = (await navigator.serviceWorker.getRegistration?.()) || allRegs[0] || null;
    if (reg) {
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try {
          await apiFetch('/api/push/unsubscribe', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ endpoint: sub.endpoint }),
          }).catch(() => {});
        } finally {
          try {
            await sub.unsubscribe();
          } catch {
            // ignore
          }
        }
      }
    }

    if (unregisterServiceWorker) {
      try {
        const regs2 = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs2.map((r) => r.unregister().catch(() => {})));
      } catch {
        // ignore
      }
    }
  } catch {
    // ignore
  }

  return { ok: true };
}

// ──────────────────────────────────────────────────────────────
// Internal helper: store subscription on server (requires session)
// ──────────────────────────────────────────────────────────────
async function persistSubscription(sub) {
  try {
    const doPost = () => apiFetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // link sub to the logged-in user
      body: JSON.stringify(sub), // PushSubscription.toJSON()
    });

    let res = await doPost();

    // If auth isn't ready yet (common on first mobile load), do a single session refresh + retry.
    if (!res.ok && (res.status === 401 || res.status === 403)) {
      await refreshSessionUser().catch(() => null);
      res = await doPost();
    }

    // If we're still unauthenticated, treat it as a transient "auth not ready" state.
    // Home page should NOT show this as an error card; we'll retry later.
    if (!res.ok && (res.status === 401 || res.status === 403)) {
      return { ok: false, reason: 'auth', status: res.status, transient: true };
    }

    if (!res.ok) {
      return { ok: false, reason: 'server', status: res.status };
    }
    return { ok: true };
  } catch (e) {
    console.warn('[push] persistSubscription error', e);
    return { ok: false, reason: 'server' };
  }
}
